'use client';

import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface AccordionItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
  icon?: React.ReactNode;
}

export const AccordionItem: React.FC<AccordionItemProps> = ({
  question,
  answer,
  isOpen,
  onToggle,
  icon,
}) => (
  <div className="border border-v2-neutral-200 rounded-v2-lg overflow-hidden bg-v2-neutral-0">
    <button
      className="w-full flex items-center gap-4 px-5 py-4 text-left hover:bg-v2-green-50 transition-colors duration-150"
      onClick={onToggle}
      aria-expanded={isOpen}
    >
      {icon && (
        <span className="shrink-0 w-9 h-9 rounded-v2-full bg-v2-green-50 flex items-center justify-center text-v2-green-500">
          {icon}
        </span>
      )}
      <h4 className="flex-1 font-semibold font-display text-[18px] text-v2-neutral-800">{question}</h4>
      <ChevronDown
        size={18}
        strokeWidth={2}
        className={`shrink-0 transition-transform duration-200 text-v2-neutral-600 ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
    <div
      className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-[500px] opacity-100 pt-2' : 'max-h-0 opacity-0'}`}
    >
      <p className="px-5 pb-5 text-[14px] font-sans text-v2-neutral-500 leading-relaxed">{answer}</p>
    </div>
  </div>
);

interface AccordionProps {
  items: { question: string; answer: string; icon?: React.ReactNode }[];
  allowMultiple?: boolean;
}

export const Accordion: React.FC<AccordionProps> = ({ items, allowMultiple = false }) => {
  const [openIndexes, setOpenIndexes] = useState<number[]>([]);

  const toggle = (index: number) => {
    if (allowMultiple) {
      setOpenIndexes((prev) =>
        prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
      );
    } else {
      setOpenIndexes((prev) => (prev.includes(index) ? [] : [index]));
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {items.map((item, i) => (
        <AccordionItem
          key={i}
          question={item.question}
          answer={item.answer}
          icon={item.icon}
          isOpen={openIndexes.includes(i)}
          onToggle={() => toggle(i)}
        />
      ))}
    </div>
  );
};

